import { useEffect, useState } from 'react';
import { canWriteEmpresaData } from '../../auth/session';
import { createItem, deleteItem, fetchList, updateItem } from './api';
import CrudModal from '../../components/layout/CrudModal';
import { formatIntegerBR, normalizeIntegerInputBR, parseIntegerBR } from '../../utils/numberBR';
import { toast } from 'react-hot-toast';

const EMPTY = {
  codigo: '',
  descricao: '',
  tipo: 'despesa',
  nivel: '1',
  dre_grupo: '',
  codigo_contabil_integracao: '',
};

const ConstrutorContasContabeisPage = () => {
  const canWrite = canWriteEmpresaData();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchList('/api/construtor/contas-contabeis', { q, page, pageSize: 10 });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar contas contabeis');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [page]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      nivel: parseIntegerBR(form.nivel) ?? 1,
      dre_grupo: form.dre_grupo || null,
      codigo_contabil_integracao: form.codigo_contabil_integracao || '',
    };
    try {
      if (editingId) {
        await updateItem('/api/construtor/contas-contabeis', editingId, payload);
        toast.success('Conta contabil atualizada');
      } else {
        await createItem('/api/construtor/contas-contabeis', payload);
        toast.success('Conta contabil salva');
      }
      setEditingId('');
      setForm(EMPTY);
      setShowFormModal(false);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar conta contabil');
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Excluir conta contabil?')) return;
    try {
      await deleteItem('/api/construtor/contas-contabeis', id);
      toast.success('Conta contabil excluida');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao excluir conta contabil');
    }
  };

  const onEdit = (item) => {
    setEditingId(item.id);
    setForm({
      codigo: item.codigo || '',
      descricao: item.descricao || '',
      tipo: item.tipo || 'despesa',
      nivel: item.nivel == null ? '1' : formatIntegerBR(item.nivel),
      dre_grupo: item.dre_grupo || '',
      codigo_contabil_integracao: item.codigo_contabil_integracao || '',
    });
    setShowFormModal(true);
  };

  const totalPages = Math.max(1, Math.ceil(total / 10));

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Contas Contabeis</h1>
        {canWrite && (
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              setEditingId('');
              setForm(EMPTY);
              setShowFormModal(true);
            }}
          >
            Incluir Conta
          </button>
        )}
      </div>

      {canWrite && (
        <CrudModal
          open={showFormModal}
          title={editingId ? 'Editar Conta Contabil' : 'Nova Conta Contabil'}
          onClose={() => {
            setShowFormModal(false);
            setEditingId('');
            setForm(EMPTY);
          }}
          maxWidthClass="max-w-5xl"
        >
          <form onSubmit={onSubmit} className="space-y-5">
            <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
              <p className="text-sm font-semibold text-gray-800">Integracao Contabil</p>
              <p className="text-xs text-gray-500 mt-1">Configure contas contabeis do construtor para integracao externa.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="form-label">Codigo</label>
                <input className="form-input" placeholder="Ex.: 1.01.01" value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Descricao</label>
                <input className="form-input" placeholder="Descricao da conta" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Tipo</label>
                <select className="form-select" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                  <option value="receita">Receita</option>
                  <option value="despesa">Despesa</option>
                  <option value="ativo">Ativo</option>
                  <option value="passivo">Passivo</option>
                </select>
              </div>
              <div>
                <label className="form-label">Nivel</label>
                <input
                  className="form-input"
                  inputMode="numeric"
                  placeholder="1"
                  value={form.nivel}
                  onChange={(e) => setForm({ ...form, nivel: normalizeIntegerInputBR(e.target.value) })}
                />
              </div>
              <div>
                <label className="form-label">Grupo DRE</label>
                <input className="form-input" placeholder="Opcional" value={form.dre_grupo} onChange={(e) => setForm({ ...form, dre_grupo: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Codigo Contabil Integracao</label>
                <input
                  className="form-input"
                  maxLength={15}
                  placeholder="Maximo 15 caracteres"
                  value={form.codigo_contabil_integracao}
                  onChange={(e) => setForm({ ...form, codigo_contabil_integracao: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t pt-4">
              <button
                className="btn-secondary"
                type="button"
                onClick={() => {
                  setShowFormModal(false);
                  setEditingId('');
                  setForm(EMPTY);
                }}
              >
                Cancelar
              </button>
              <button className="btn-primary" type="submit">{editingId ? 'Atualizar Conta' : 'Salvar Conta'}</button>
            </div>
          </form>
        </CrudModal>
      )}

      <div className="card">
        <div className="card-header flex items-center gap-3">
          <input className="form-input max-w-sm" placeholder="Buscar conta..." value={q} onChange={(e) => setQ(e.target.value)} />
          <button className="btn-secondary" onClick={() => { setPage(1); load(); }}>Buscar</button>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Codigo</th>
                  <th>Descricao</th>
                  <th>Tipo</th>
                  <th>Cod. Integracao</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id}>
                    <td>{it.codigo}</td>
                    <td>{it.descricao}</td>
                    <td>{it.tipo}</td>
                    <td>{it.codigo_contabil_integracao || '-'}</td>
                    <td>
                      {canWrite && <button className="btn-secondary mr-2" onClick={() => onEdit(it)}>Editar</button>}
                      {canWrite && <button className="btn-danger" onClick={() => onDelete(it.id)}>Excluir</button>}
                    </td>
                  </tr>
                ))}
                {!loading && items.length === 0 && <tr><td colSpan={5} className="text-center text-gray-500 py-6">Sem dados</td></tr>}
                {loading && <tr><td colSpan={5} className="text-center text-gray-500 py-6">Carregando...</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-body border-t flex items-center justify-between">
          <span className="text-sm text-gray-600">Pagina {page} de {totalPages} ({total} registros)</span>
          <div className="flex gap-2">
            <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</button>
            <button className="btn-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Proxima</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConstrutorContasContabeisPage;
